import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import CyvEditor from '../../../components/Editor/CyvEditor';
import {
	CAPABILITIES_DOCS,
	CAPABILITIES_SCRIPT,
} from '../../../components/Editor/editorCapabilities';
import DocsService from '../../../services/DocsService';
import { DOC_TYPES } from '../../../services/docsTemplates';
import { containsScriptNodes } from '../../../services/scriptFormat';
import { primeEntityCache } from '../../../services/entityMentionProviders';
import { indexById, breadcrumbFor } from '../../../services/docsTree';
import './DocEditor.css';

const AUTOSAVE_MS = 800;

/* Docs v1 — the full-page editor.
   Autosave is a debounced single-document overwrite (see DocsService for why
   that shape specifically). Save status is surfaced honestly: "Saving…" while a
   write is in flight, an explicit error if it fails — never a silent drop. */

function DocEditor() {
	const { docId } = useParams();
	const navigate = useNavigate();
	const { currentUser } = useAuth();

	const [initialContent, setInitialContent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [status, setStatus] = useState('idle'); // idle | saving | saved | error
	const [title, setTitle] = useState(null);
	const [docMeta, setDocMeta] = useState(null); // carries ancestors[] for crumbs
	const [allDocs, setAllDocs] = useState([]);

	const timer = useRef(null);
	const pending = useRef(null);
	const mounted = useRef(true);

	// Warm the @-mention entity cache while the user is still reading, so the
	// first "@" doesn't stall on four Firestore reads.
	useEffect(() => {
		primeEntityCache();
	}, []);

	// The doc list backs the breadcrumb trail. Subscribed rather than fetched so
	// renaming an ancestor in another tab updates the crumbs here.
	useEffect(() => {
		const unsub = DocsService.subscribeToDocs(
			setAllDocs,
			(err) => console.warn('[Docs] breadcrumb list unavailable', err?.code || err)
		);
		return unsub;
	}, []);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Load once (not a live subscription): a snapshot stream would fight the
	// local editor state on every autosave echo. Single-writer by design in v1.
	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		DocsService.fetchDoc(docId)
			.then((d) => {
				if (cancelled) return;
				if (!d) {
					setNotFound(true);
				} else {
					setInitialContent(d.content || null);
					setTitle(d.title || null);
					setDocMeta(d);
				}
				setLoading(false);
			})
			.catch((err) => {
				console.error('[Docs] load failed', err);
				if (!cancelled) {
					setNotFound(true);
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [docId]);

	const flush = useCallback(async () => {
		if (!pending.current) return;
		const json = pending.current;
		pending.current = null;
		setStatus('saving');
		try {
			await DocsService.saveDocContent(docId, json, { uid: currentUser?.uid });
			if (mounted.current) setStatus('saved');
		} catch (err) {
			console.error('[Docs] save failed', err);
			if (mounted.current) setStatus('error');
		}
	}, [docId, currentUser]);

	const handleChange = useCallback(
		({ json }) => {
			pending.current = json;
			setStatus('saving');
			if (timer.current) clearTimeout(timer.current);
			timer.current = setTimeout(flush, AUTOSAVE_MS);
		},
		[flush]
	);

	// Flush on unmount so navigating away mid-debounce never loses the last edit.
	useEffect(() => {
		return () => {
			if (timer.current) clearTimeout(timer.current);
			if (pending.current) {
				DocsService.saveDocContent(docId, pending.current, {
					uid: currentUser?.uid,
				}).catch((err) => console.error('[Docs] final save failed', err));
			}
		};
	}, [docId, currentUser]);

	const handleEntityClick = ({ to }) => {
		if (to) navigate(to);
	};

	/* Is this a script? Trust the stored type, but ALSO trust the content.
	 *
	 * Capabilities gate extensions, so opening a doc that holds script nodes
	 * WITHOUT the script capability does not error — ProseMirror silently drops
	 * the unknown nodes, and the autosave right behind it writes the stripped
	 * document back over the original. A doc whose `type` is missing or wrong
	 * (an old copy, a hand-edited record) would destroy itself just by being
	 * opened. The content check makes that impossible.
	 */
	const isScript = useMemo(
		() => docMeta?.type === DOC_TYPES.SCRIPT || containsScriptNodes(initialContent),
		[docMeta, initialContent]
	);

	/* Inline sub-page wiring.
	   The handler object must keep a STABLE identity (it is read through a ref
	   inside the node view), so the live doc list travels via a mutable box and
	   listeners are notified explicitly whenever it changes. Without that the
	   node resolved its title exactly once and never again — a rename would
	   never propagate. */
	const childBox = useRef({ docs: [], listeners: new Set() });
	childBox.current.docs = allDocs;

	useEffect(() => {
		childBox.current.listeners.forEach((fn) => {
			try {
				fn();
			} catch (err) {
				console.error('[Docs] child-page listener failed', err);
			}
		});
	}, [allDocs]);

	const childPage = useMemo(
		() => ({
			// `undefined` = unknown (not loaded, or too new to be in the snapshot).
			// Never assume deletion here; the node view confirms via verifyExists.
			resolveTitle: (id) => {
				const found = childBox.current.docs.find((d) => d.id === id);
				return found ? found.title : undefined;
			},
			verifyExists: async (id) => {
				try {
					return !!(await DocsService.fetchDoc(id));
				} catch {
					return true; // a failed read is not evidence of deletion
				}
			},
			subscribe: (cb) => {
				const set = childBox.current.listeners;
				set.add(cb);
				return () => set.delete(cb);
			},
			onOpen: (id) => navigate(`/portal/work/docs/${id}`),
			onOpenNewTab: (id) =>
				window.open(`/portal/work/docs/${id}`, '_blank', 'noopener'),
			createChild: async () => {
				const newId = await DocsService.createDoc({
					uid: currentUser?.uid,
					displayName: currentUser?.displayName,
					parentId: docId,
				});
				return { id: newId, title: null };
			},
		}),
		[navigate, currentUser, docId]
	);

	if (loading) return <div className='doc-editor-page'><div className='doc-editor__state'>Loading…</div></div>;

	if (notFound) {
		return (
			<div className='doc-editor-page'>
				<div className='doc-editor__state'>
					<p>That doc doesn’t exist, or you don’t have access to it.</p>
					<Link to='/portal/work/docs'>Back to Docs</Link>
				</div>
			</div>
		);
	}

	return (
		<div className={`doc-editor-page ${isScript ? 'doc-editor-page--script' : ''}`.trim()}>
			<div className='doc-editor__bar'>
				<Link to='/portal/work/docs' className='doc-editor__back'>
					<i className='fa-solid fa-arrow-left' aria-hidden='true'></i>
					<span>Docs</span>
				</Link>
				<nav className='doc-editor__crumbs' aria-label='Breadcrumb'>
					{breadcrumbFor(docMeta, indexById(allDocs)).map((c) => (
						<React.Fragment key={c.id}>
							<Link to={`/portal/work/docs/${c.id}`} className='doc-editor__crumb'>
								{c.title || 'Untitled'}
							</Link>
							<span className='doc-editor__crumb-sep' aria-hidden='true'>
								/
							</span>
						</React.Fragment>
					))}
				</nav>
				<span
					className={`doc-editor__status doc-editor__status--${status}`}
					aria-live='polite'>
					{status === 'saving' && 'Saving…'}
					{status === 'saved' && 'Saved'}
					{status === 'error' && 'Not saved — retrying on next edit'}
				</span>
			</div>

			<div className='doc-editor__sheet'>
				{/* The keymap IS the feature, so the page says so rather than
				    leaving it to be discovered or remembered. */}
				{isScript && (
					<p className='doc-editor__scripthint'>
						<span>
							<kbd>Tab</kbd> changes element
						</span>
						<span>
							<kbd>Shift</kbd> + <kbd>Tab</kbd> goes back
						</span>
						<span>
							<kbd>Enter</kbd> continues — character, then dialogue
						</span>
						<span>
							<kbd>Enter</kbd> on a blank line steps out
						</span>
					</p>
				)}
				<CyvEditor
					content={initialContent}
					onChange={handleChange}
					onEntityClick={handleEntityClick}
					capabilities={isScript ? CAPABILITIES_SCRIPT : CAPABILITIES_DOCS}
					childPage={childPage}
					autofocus={!title}
					placeholder='Write something, or press “/” for blocks…'
				/>
			</div>
		</div>
	);
}

export default DocEditor;
